import type { KeyboardEvent, ReactNode } from "react";
import { cn } from "@/shared/lib/cn";

export interface FreeTextControlProps {
  value: string;
  onChange: (next: string) => void;
  max?: number;
  rows?: number;
  placeholder?: string;
  readOnly?: boolean;
  /// Optional reassurance shown left of the character counter (e.g. the pulse
  /// redaction note). Omitted for non-anonymous contexts like reviews.
  privacyNote?: ReactNode;
  /// Forwarded to the textarea, e.g. for a Cmd/Ctrl+Enter submit shortcut.
  onKeyDown?: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
  /// Optional id, placed on the textarea. `<FormControl>` is a Radix Slot: it
  /// stamps the field id on its immediate child, so without this prop the id
  /// was dropped and `<FormLabel htmlFor>` named an id nothing carried. The
  /// textarea is the one control a label should focus, so it takes the id, the
  /// way `DatePicker` already does.
  id?: string;
}

/// Free-text answer control: textarea with a character counter and an optional
/// privacy note. Presentational (value / onChange); shared by pulse surveys
/// (free_text) and performance reviews (text).
export function FreeTextControl({
  value,
  onChange,
  max = 5000,
  rows = 4,
  placeholder = "Type your answer.",
  readOnly,
  privacyNote,
  onKeyDown,
  id,
}: FreeTextControlProps): ReactNode {
  return (
    <div>
      <textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        rows={rows}
        maxLength={max}
        readOnly={readOnly}
        className={cn(
          "block min-h-[110px] w-full rounded-lg bg-surface-base px-4 py-3.5 text-sm leading-relaxed text-text-primary ring-1 ring-inset ring-border-strong placeholder:text-text-quaternary focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none",
          readOnly && "opacity-70",
        )}
      />
      <div
        className={cn(
          "mt-2 flex text-2xs text-text-tertiary",
          privacyNote ? "justify-between" : "justify-end",
        )}
      >
        {privacyNote ? (
          <span className="inline-flex items-center gap-1.5">{privacyNote}</span>
        ) : null}
        <span className="font-mono tabular-nums">
          {value.length}/{max}
        </span>
      </div>
    </div>
  );
}
